import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Link2, Loader2, Upload } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

interface AddImageDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Imports the file or link as a source image; resolves once it is on the canvas. */
    onImport: (source: { file: File } | { url: string }) => Promise<void>;
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif';

/**
 * Bring an image onto the canvas as a source node — a room photo, a shot from
 * a shoot, a picture found online. It becomes an image like any generated one:
 * Variation, Edit, Video and Place in a room all work off it.
 */
export const AddImageDialog: React.FC<AddImageDialogProps> = ({ open, onOpenChange, onImport }) => {
    const { t } = useTranslation();
    const fileRef = useRef<HTMLInputElement>(null);
    const [url, setUrl] = useState('');
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (source: { file: File } | { url: string }) => {
        setBusy(true);
        setError(null);
        try {
            await onImport(source);
            setUrl('');
            onOpenChange(false);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message
                || (t('CONTENT_STUDIO.ADD_IMAGE.FAILED', 'The image could not be added.') as string));
        } finally {
            setBusy(false);
        }
    };

    const onFiles = (files: FileList | null) => {
        const file = files?.[0];
        if (file) void run({ file });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
            <DialogContent className="max-w-md">
                <DialogTitle className="text-base font-medium">
                    {t('CONTENT_STUDIO.ADD_IMAGE.TITLE', 'Add an image')}
                </DialogTitle>
                <DialogDescription className="text-xs">
                    {t('CONTENT_STUDIO.ADD_IMAGE.HINT', 'It becomes a source on the canvas — vary it, edit it, animate it, or place your furniture in it.')}
                </DialogDescription>

                <button
                    type="button"
                    disabled={busy}
                    onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); onFiles(e.dataTransfer.files); }}
                    className={cn(
                        'flex h-36 w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted text-sm text-muted-foreground transition-colors',
                        dragOver ? 'border-foreground text-foreground' : 'hover:border-foreground/40 hover:text-foreground',
                    )}
                >
                    {busy ? <Loader2 className="size-5 animate-spin" /> : <Upload className="size-5" />}
                    {t('CONTENT_STUDIO.ADD_IMAGE.DROP', 'Drop an image here, or click to choose')}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
                />

                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="h-px flex-1 bg-border" />
                    {t('CONTENT_STUDIO.ADD_IMAGE.OR', 'or')}
                    <span className="h-px flex-1 bg-border" />
                </div>

                <form
                    className="flex gap-2"
                    onSubmit={(e) => { e.preventDefault(); if (url.trim()) void run({ url: url.trim() }); }}
                >
                    <div className="relative flex-1">
                        <Link2 className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder={t('CONTENT_STUDIO.ADD_IMAGE.LINK_PLACEHOLDER', 'Paste an image link') as string}
                            className="h-9 ps-8"
                            disabled={busy}
                        />
                    </div>
                    <Button type="submit" variant="outline" className="h-9 gap-1.5" disabled={busy || !url.trim()}>
                        <ImagePlus className="size-4" />
                        {t('CONTENT_STUDIO.ADD_IMAGE.IMPORT', 'Import')}
                    </Button>
                </form>

                {error && <p className="text-xs text-destructive">{error}</p>}
            </DialogContent>
        </Dialog>
    );
};
